'use client';

import Link from 'next/link';
import { useLayoutEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useAuthModal } from '@/hooks/useAuthModal';
import { useStore } from '@/providers/StoreProvider';
import { AUTH_MESSAGES } from '@repo/utils';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/cn';
import { LogoLink } from '../shared/LogoLink';
import { analyticsDataAttrs } from '@/lib/analytics/data-attrs';
import { IconUser, IconEdit } from '@repo/svgs';
import { Badge, Button, Skeleton } from '../ui';

const containerClasses = 'mx-auto flex w-full items-center justify-between px-12 py-6 h-header';

export function Header() {
  const analytics = useAnalytics();
  const { openAuthFlow } = useAuthModal();
  const pathname = usePathname();
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const profile = useStore(s => s.profile?.profile);
  const sessionHydrated = useStore(s => s.webAuth.sessionHydrated);
  const explorer = useStore(s => s.explorer.data);
  const headerNavEnabled = useStore(s => s.headerNav.enabled);
  const unreadMessages = useStore(s => s.chat?.unreadMessages || 0);
  // Hard guarantee: application profile flow should never show the desktop header nav links/auth button.
  // (We also support the reusable HeaderNavVisibility toggle for other flows.)
  const isApplicationProfile = pathname.startsWith('/profile/application-profile');
  const showNav = headerNavEnabled && !isApplicationProfile;
  const isHome = pathname === '/' || pathname === '/read-reviews';
  const isReviewPage = pathname === '/review';

  const handleLoginClick = () => {
    analytics.track('header_menu_open');
    void openAuthFlow(AUTH_MESSAGES.DEFAULT).catch(() => {
      // Auth flow was cancelled or rejected - this is expected behavior
    });
  };

  const getInitials = () => {
    if (!profile) return '';
    // UserProfile type uses camelCase after transformation
    const firstName =
      'firstName' in profile && typeof profile.firstName === 'string' ? profile.firstName : null;
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (profile.email) {
      return profile.email.charAt(0).toUpperCase();
    }
    return '';
  };

  return (
    <header
      id="site-header"
      className={cn(
        'hidden md:block w-full  relative',
        isHome ? 'bg-secondary' : 'bg-primary border-b border-secondary',
        '[&:has(#site-header-affix>[data-affixed="true"])]:border-b',
        '[&:has(#site-header-affix>[data-affixed="true"])]:border-secondary',
      )}
    >
      {/* Affix mount point for portaled search bar */}
      <div className={containerClasses}>
        <div className="flex justify-center gap-6 items-center">
          {/* Brand */}
          <LogoLink variant="horizontal" {...analyticsDataAttrs('header_logo')} />
          {/* Affix mount point for portaled search bar. Removing this element will cause the search bar to not be portaled. */}
          <div
            id="site-header-affix"
            className="group xl:absolute xl:top-1/2 xl:left-1/2 xl:-translate-x-1/2 xl:-translate-y-1/2 xl:w-fit"
          />
        </div>
        {/* Main navigation (can be disabled per-route) */}
        {showNav ? (
          <nav aria-label="Primary" className="flex items-center gap-2">
            {!sessionHydrated ? (
              <div aria-hidden className="flex items-center gap-2 pointer-events-none">
                <Skeleton className="h-10 w-16 rounded-full" />
                <Skeleton className="h-10 w-24 rounded-full" />
                <Skeleton className="h-10 w-16 rounded-full" />
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>
            ) : (
              <>
                {!isReviewPage && (
                  <Link
                    href="/review"
                    onClick={() => analytics.track('header_review')}
                    className="rounded-full text-body-2-high text-accent transition-colors hover:bg-secondary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 flex items-center gap-2"
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      className="cursor-pointer"
                      iconLeft={<IconEdit className="w-4 h-4 icon-accent" aria-hidden />}
                    >
                      Review
                    </Button>
                  </Link>
                )}
                <Link
                  href="/messages"
                  onClick={() => analytics.track('header_messages')}
                  className="rounded-full relative px-3 py-2 text-body-2 text-primary transition-colors hover:bg-secondary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  Messages
                  {unreadMessages > 0 && (
                    <Badge
                      type="alert"
                      count={unreadMessages}
                      border
                      className="!absolute -top-2 -right-1"
                    />
                  )}
                </Link>
                <Link
                  href="/profile/tenant-portal"
                  onClick={() => analytics.track('header_lease')}
                  className="rounded-full  px-3 py-2 text-body-2 text-primary transition-colors hover:bg-secondary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  Lease
                </Link>

                {profile?.id ? (
                  <Link
                    href="/profile"
                    className="w-11 h-11 relative rounded-full bg-active flex items-center justify-center transition-colors hover:bg-active-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                    aria-label="Profile"
                    onClick={() => analytics.track('header_menu_open')}
                  >
                    {(!explorer?.accessType || !isLoggedIn) && (
                      <Badge type="alert" border className="!absolute top-0.5 right-0.5" />
                    )}
                    <span className="text-body-2-high text-primary font-semibold">
                      {getInitials()}
                    </span>
                  </Link>
                ) : (
                  <Link
                    href="/profile"
                    className="w-10 h-10 rounded-full cursor-pointer relative text-body-2-high text-accent transition-colors hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 flex items-center justify-center"
                  >
                    {(!explorer?.accessType || !isLoggedIn) && (
                      <Badge type="alert" border className="!absolute top-0.5 right-0.5" />
                    )}
                    <IconUser className="w-4 h-4 icon-primary" />
                  </Link>
                )}
              </>
            )}
          </nav>
        ) : null}
      </div>
    </header>
  );
}

interface HeaderNavVisibilityProps {
  enabled: boolean;
}

export function HeaderNavVisibility({ enabled }: HeaderNavVisibilityProps) {
  const setEnabled = useStore(s => s.headerNav.setEnabled);

  // Use layout effect so the desktop header nav is hidden before paint (avoids flicker).
  useLayoutEffect(() => {
    setEnabled(enabled);
    return () => {
      setEnabled(true);
    };
  }, [enabled, setEnabled]);

  return null;
}
